import React, {useEffect, useState} from "react";

import {useTranslation} from "react-i18next";
import Typography from "@mui/material/Typography";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  FileUpload,
} from '@vedmarket/ui-kit';
import {useFormContext} from "react-hook-form";
import IconButton from "@mui/material/IconButton";
import {PACKAGE_FORM, PackageForm} from "../add_form/helpers";
import TextField from "@mui/material/TextField";

import {COLORS} from "../../../../../../../../../../../../../config/constants";

import './manufacturer.scss';
import InputAdornment from "@mui/material/InputAdornment";
import {useUploadImageMutation} from "../../../../../../../../../../../../../api/media_storage";

const Manufacturer = ({ initialPhoto, disabled, onVisibilityChange }: ManufacturerProps) => {

  const { t } = useTranslation()

  const [ showManufacturerInfo, setShowManufacturerInfo ] = useState(false);
  useEffect(() => { onVisibilityChange?.(showManufacturerInfo); }, [ showManufacturerInfo, onVisibilityChange ]);
  const [ uploadedFileTitle, setUploadedFileTitle ] = useState('');
  const [ uploadedPhotoUrl, setUploadedPhotoUrl ] = useState('');

  const { register, getValues, setValue, watch, formState: { errors }  } = useFormContext<PackageForm>()
  const [ uploadImage ] = useUploadImageMutation()

  const handleClickOpenManufacturer = () => {
    if (disabled) {
      return
    }

    setShowManufacturerInfo(!showManufacturerInfo);
  }

  const handleRemoveManufacturer = () => {
    if (disabled) {
      return
    }

    setValue(PACKAGE_FORM.MANUFACTURER, '', { shouldDirty: true, shouldTouch: true });
    setValue(PACKAGE_FORM.DESCRIPTION, '', { shouldDirty: true, shouldTouch: true });
    handleRemoveFile();
  }

  const hasData = getValues()[PACKAGE_FORM.DESCRIPTION]
    || getValues()[PACKAGE_FORM.MANUFACTURER]
    || getValues()[PACKAGE_FORM.PRODUCT_PHOTO];

  useEffect(() => {
    if (!showManufacturerInfo && hasData) {
      setShowManufacturerInfo(true);
    }
  }, [ hasData ])

  useEffect(() => {
    setUploadedPhotoUrl(initialPhoto?.url || '');
  }, [ initialPhoto?.url ])

  const description = watch(PACKAGE_FORM.DESCRIPTION);
  const manufacturer = watch(PACKAGE_FORM.MANUFACTURER);
  const productPhoto = watch(PACKAGE_FORM.PRODUCT_PHOTO);

  const handleChangeFile = async (file: File) => {
    if (disabled) {
      return false
    }

    const formData = new FormData();

    formData.append('title', 'Фото товара');
    formData.append('image', file);

    const uploadedImage = await uploadImage(formData)
      .unwrap()
      .catch(() => null);

    if (!uploadedImage?.id) {
      return false;
    }

    setValue(PACKAGE_FORM.PRODUCT_PHOTO, uploadedImage.id, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setUploadedFileTitle(file.name);
    setUploadedPhotoUrl(uploadedImage.image || '');

    return true;
  }

  const handleRemoveFile = () => {
    if (disabled) {
      return
    }

    setValue(PACKAGE_FORM.PRODUCT_PHOTO, null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setUploadedFileTitle('');
    setUploadedPhotoUrl('');
  }

  const hasRemoveButton = Boolean(description || manufacturer || productPhoto);

  return (
    <div className='invoice-form__manufacturer manufacturer'>
      <Typography variant='body1' sx={{
        color: disabled ? COLORS.LIGHT_TEXT_DISABLED : COLORS.BLUE,
        display: 'flex',
        alignItems: 'flex-start',
        cursor: disabled ? 'default' : 'pointer',
        lineHeight: '24px',
        letterSpacing: 0,
      }}
      onClick={handleClickOpenManufacturer}
      >
        <AddIcon style={{
          marginRight: '8px',
          flexShrink: 0,
          fill: 'currentColor',
        }}/>
        {t('calc__text__packaging__addManufacturerAndSpecificationNotRequired')}
      </Typography>
      {showManufacturerInfo && <div className='manufacturer__info'>
        <div className='manufacturer__name-row'>
          <TextField
            className='manufacturer__name'
            {...register(PACKAGE_FORM.DESCRIPTION)}
            error={Boolean(errors[PACKAGE_FORM.DESCRIPTION])}
            helperText={errors[PACKAGE_FORM.DESCRIPTION]?.message}
            variant='outlined'
            label={t('calc__text__packaging__addManufacturerNotRequired')}
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
            InputProps={{
              endAdornment: description && !disabled ? (
                <InputAdornment position='end'>
                  <CloseIcon
                    style={{
                      width: 24,
                      height: 24,
                      cursor: 'pointer',
                    }}
                    onClick={() => setValue(PACKAGE_FORM.DESCRIPTION, '', { shouldDirty: true, shouldTouch: true })}
                  />
                </InputAdornment>
              ) : null,
            }}
          />
          {hasRemoveButton && <IconButton
            onClick={handleRemoveManufacturer}
            disabled={disabled}
            aria-label={t('actions__text__delete')}
            sx={{
              width: '32px',
              height: '32px',
              padding: '4px',
              flexShrink: 0,
            }}
          >
            <DeleteIcon style={{fill: 'currentColor'}}/>
          </IconButton>}
        </div>
        <TextField
          className='manufacturer__specification'
          {...register(PACKAGE_FORM.MANUFACTURER)}
          error={Boolean(errors[PACKAGE_FORM.MANUFACTURER])}
          helperText={errors[PACKAGE_FORM.MANUFACTURER]?.message}
          variant='outlined'
          label={t('calc__text__packaging__addSpecificationNotRequired')}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
          multiline
          minRows={2}
        />
        {!uploadedPhotoUrl && <FileUpload
          acceptedFilesConfig={{
            "image/*": [ ".gif", ".png", ".svg", ".jpeg", ".jpg" ],
          }}
          initialFileTitle={String(uploadedFileTitle || '').trim()}
          onHandleFile={handleChangeFile}
          onRemove={handleRemoveFile}
          title={t('calc__text__packaging__addPhotoNotRequired')}
        />}
        {uploadedPhotoUrl && <div className='manufacturer__photo'>
          <div className='manufacturer__photo-button'>
            <img
              alt={uploadedFileTitle || initialPhoto?.title || t('calc__text__packaging__addPhoto')}
              className='manufacturer__photo-image'
              src={uploadedPhotoUrl}
            />
          </div>
          <div>
            <IconButton
              className='manufacturer__photo-remove'
              onClick={handleRemoveFile}
              disabled={disabled}
            >
              <DeleteIcon style={{
                color: COLORS.LIGHT_ACTION
              }}/>
            </IconButton>
          </div>
        </div>}
      </div>}
    </div>
  )
}

export default Manufacturer
